/* ════════════════════════════════════════════════════════════
   Tournées d'une commande.

   Une commande réunissant plusieurs camions peut être livrée en
   plusieurs fois, à des dates différentes. Chaque camion est une
   tournée.

   Une commande sans tournée reste pilotée par `orders.date_livraison`,
   qui demeure le cas normal — un camion, une date. Dès qu'il y a des
   tournées, cette colonne recopie la date de la première, pour que les
   écrans qui la lisent continuent de dire quelque chose de juste.
   ════════════════════════════════════════════════════════════ */

import { supabase } from '../../supabase'
import { CAPACITE_CAMION } from './carnetUtils'

/* Découpe proposée à partir des lignes de la commande.

   Une marchandise ne se mélange pas à une autre dans la même remorque :
   chaque ligne occupe donc ses propres camions, et un produit dépassant
   la capacité est réparti sur plusieurs.

   `total` permet d'imposer un nombre de camions — celui saisi à la main
   sur la commande. Sans lui, la capacité seule décide. Les camions en
   plus vont aux lignes les plus chargées, de sorte que la répartition
   reste équilibrée plutôt que de tout ajouter au premier produit. */
export function decouper(lignes, { total, capacite = CAPACITE_CAMION } = {}) {
  const parts = (lignes || [])
    .map((l) => ({ produit: l.produit, poids: parseFloat(l.poids_t ?? l.poids) || 0 }))
    .filter((l) => l.poids > 0)
    .map((l) => ({ ...l, nb: Math.max(1, Math.ceil(l.poids / capacite)) }))

  if (parts.length === 0) return []

  /* Un camion demandé de moins que le minimum imposé par la capacité
     est ignoré : il produirait des remorques en surcharge. */
  const demande = parseInt(total, 10)
  if (Number.isFinite(demande)) {
    let restant = demande - parts.reduce((s, p) => s + p.nb, 0)
    while (restant > 0) {
      parts.sort((a, b) => b.poids / b.nb - a.poids / a.nb)[0].nb += 1
      restant -= 1
    }
  }

  const camions = []
  parts.forEach((p) => {
    const part = p.poids / p.nb
    for (let i = 0; i < p.nb; i++) {
      camions.push({ produits: p.produit, poids_t: Math.round(part * 100) / 100 })
    }
  })
  return camions.map((c, i) => ({ ...c, rang: i + 1, date_livraison: null, statut: 'prevue' }))
}

/* Date portée par la commande : la première tournée calée.

   Pas la plus petite date au hasard — celle du rang le plus faible
   parmi celles qui ont un jour, pour que l'ordre voulu soit respecté
   même si les dates ont été saisies dans le désordre. */
export function dateDeCommande(tournees) {
  const calees = (tournees || []).filter((t) => t.date_livraison)
  if (calees.length === 0) return null
  return calees.sort((a, b) => new Date(a.date_livraison) - new Date(b.date_livraison))[0].date_livraison
}

/* Recopie la date sur la commande. Appelée après toute modification
   d'une tournée : sans elle, le carnet et les statistiques resteraient
   sur une date périmée. */
export async function synchroniserCommande(orderId) {
  const { data } = await supabase.from('tournees')
    .select('date_livraison, rang').eq('order_id', orderId)
  return supabase.from('orders')
    .update({ date_livraison: dateDeCommande(data) })
    .eq('id', orderId)
}
